import React, { useContext, useMemo, useState } from 'react';
import styles from './MfCreateNewComp.module.css';
import ModalInputField from 'components/shared/modal/ModalInputField';
import ModalDropdown from 'components/shared/modal/ModalDropdown';
import AuthenticationContext from 'config/context/authenticationContext';
import CompositionRecord from '../../composition-record/CompositionRecord';
import { ArrowRightIcon, PlusIcon } from 'features/compositions/assets/icons/icons';
import AddObject from '../../composition-record/AddObject';
import dataTypesConfig from 'features/compositions/constants/dataTypes.json';
import AddRecord from '../../composition-record/AddRecord';
import { useMfImport } from 'features/compositions/context/MfImportContext';
import { validateCompositionName } from 'utils/validations';

const MfCreateNewComp = () => {
    const { permissionsList } = useContext(AuthenticationContext);
    const { table, setTable, setStep, setStructs, selectedComposition, setSelectedComposition, selectedPermission, setSelectedPermission, allCompositionsList, fileType } = useMfImport();

    const [view, setView] = useState('records');
    const [nra, setNra] = useState(1); // 3=Add field,4=Add index,5=Add array
    const [recordTypes, setRecordTypes] = useState(dataTypesConfig.dataTypes);
    const [draggedItemIndex, setDraggedItemIndex] = useState(null);


    const normalizeAIFlag = (fields) => {
        const firstIndexPosition = fields.findIndex(f => f.is_index);

        return fields.map((f, i) => {
            if (!f.is_index) {
                return { ...f, ai: false };
            }

            if (i === firstIndexPosition) {
                return { ...f };
            }

            return { ...f, ai: false };
        });
    };

    const normalizeIndexFlag = (fields) => {
        return fields.map(f => {
            if (f.is_index) {
                return { ...f, is_vector: false }; // Ako je index true, vector je false
            }
            return f;
        });
    };

    const normalizeFields = (fields) => {
        const aiNormalized = normalizeAIFlag(fields);
        const indexNormalized = normalizeIndexFlag(aiNormalized);
        return indexNormalized;
    };

    const handleDragStart = (index) => setDraggedItemIndex(index);

    const handleDragOver = (e, index) => {
        e.preventDefault();
        if (draggedItemIndex === null || draggedItemIndex === index) return;

        setTable(prev => {
            const newFields = [...prev.fields];
            const [removed] = newFields.splice(draggedItemIndex, 1);
            newFields.splice(index, 0, removed);

            const normalized = normalizeFields(newFields);
            setDraggedItemIndex(index);

            return { ...prev, fields: normalized };
        });
    };

    const handleDragEnd = () => setDraggedItemIndex(null);

    const handleItemChange = (index, key, value) => {
        setTable(prev => {
            const updated = [...prev.fields];
            updated[index] = { ...updated[index], [key]: value };
            const normalized = normalizeFields(updated);
            return { ...prev, fields: normalized };
        });
    };

    const handleDeleteItem = (index) => {
        setTable(prev => {
            const updated = [...prev.fields];
            if (!updated[index]?.new) return prev;
            updated.splice(index, 1);
            return { ...prev, fields: updated };
        });
    };

    const goToAuth = () => {
        const isValid = validateCompositionName(selectedComposition, allCompositionsList);
        if (isValid) setStep(3);
    };

    const isCsv = fileType() === 'csv';

    return (
        <div className={styles.mf_create_new_comp}>
            {view === 'records' && (
                <div className={styles.mf_create_new_comp_container}>
                    <div className={styles.header}>
                        <ModalInputField
                            type="text"
                            placeholder="Composition name"
                            value={selectedComposition}
                            setValue={setSelectedComposition}
                            validator={(val) => validateCompositionName(val, allCompositionsList)}
                        />
                        <ModalDropdown
                            title={selectedPermission || "Composition permission"}
                            options={permissionsList}
                            setOption={setSelectedPermission}
                        />
                    </div>
                    <div className={styles.important_notes}>
                        <span>Note: Use Uint 64bit as the first index with AI enabled.</span>
                    </div>
                    <div className={styles.records_area}>
                        <div className={styles.records_container}>
                            <div className={styles.records_header}>
                                <span></span>
                                <span>Index</span>
                                <span>AI</span>
                                <span>Array</span>
                                <span>Name</span>
                                <span>Type</span>
                                <span>Default vaue</span>
                            </div>

                            <div className={styles.records}>
                                {table?.fields.map((item, idx) => (
                                    <CompositionRecord
                                        key={item.name}
                                        item={item}
                                        index={idx}
                                        isDragged={idx === draggedItemIndex}
                                        onDragStart={handleDragStart}
                                        onDragOver={handleDragOver}
                                        onDragEnd={handleDragEnd}
                                        onDrop={handleDragEnd}
                                        onChange={handleItemChange}
                                        onDelete={handleDeleteItem}
                                        dataTypes={recordTypes}
                                    />
                                ))}
                            </div>

                            <div className={styles.records_actions}>
                                {nra === 1 && (
                                    <div className={styles.records_actions__1}>
                                        <button onClick={() => setNra(2)}>Add new record <PlusIcon /></button>
                                        <button onClick={goToAuth}>Go to authorization <ArrowRightIcon /></button>
                                    </div>
                                )}
                                {nra === 2 && (
                                    <div className={styles.records_actions__2}>
                                        <button onClick={() => setNra(3)}>Add field</button>
                                        <button onClick={() => setNra(4)}>Add index</button>
                                        <button onClick={() => setNra(5)}>Add array</button>
                                        {!isCsv && <button onClick={() => setView('add-object')}>Add object</button>}
                                    </div>
                                )}
                                {nra === 3 && <AddRecord table={table} setTable={setTable} setNra={setNra} isIndex={false} isArray={false} dataTypes={recordTypes} />}
                                {nra === 4 && <AddRecord table={table} setTable={setTable} setNra={setNra} isIndex={true} isArray={false} dataTypes={recordTypes} />}
                                {nra === 5 && <AddRecord table={table} setTable={setTable} setNra={setNra} isIndex={false} isArray={true} dataTypes={recordTypes} />}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {view === 'add-object' && (
                <AddObject
                    dataTypes={recordTypes}
                    setDataTypes={setRecordTypes}
                    setView={setView}
                    setStructs={setStructs}
                />
            )}
        </div>
    );
};

export default MfCreateNewComp;
